import { useTranslation } from 'react-i18next';
import { AttendanceClockControl } from '@/features/attendance/AttendanceClockControl';
import { useMe, useSession } from '@/features/auth/session-context';
import { roleHasBell } from '@/features/shell/config';
import { greetingKey, greetingName } from '@/features/shell/greeting';
import { useNow } from '@/features/shell/use-now';
import { BranchSwitcher } from './BranchSwitcher';
import { GlobalSearch } from './shell/GlobalSearch';
import { ReminderBell } from './shell/ReminderBell';
import { formatTopbarDate } from './topbar-date';
import styles from './Topbar.module.css';

/**
 * Admin v2 topbar: greeting (dashboard) or page title, live date · time · role,
 * search, reminders bell (not for Lab or Mental health) and branch switch.
 */
export function Topbar({ title }: { title?: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const { hasPermission } = useSession();
  const now = useNow();
  const canClock =
    hasPermission('attendance:clock') &&
    me.activeRole.key !== 'clinic_manager' &&
    me.activeRole.key !== 'super_admin';

  return (
    <header className={styles.topbar}>
      <div className={styles.heading}>
        <h1 className={styles.title}>
          {title ??
            t(greetingKey(now), {
              name: me.greetingName ?? greetingName(me.fullName),
            })}
        </h1>
        <span className={styles.meta} data-testid="topbar-meta">
          {formatTopbarDate(now)} · {me.activeRole.name}
        </span>
      </div>
      <div className={styles.tools}>
        {canClock && <AttendanceClockControl />}
        <GlobalSearch />
        {roleHasBell(me.activeRole.key) && <ReminderBell />}
        <BranchSwitcher />
      </div>
    </header>
  );
}
